import { useState } from 'react';
import { useEditorStore } from '../../store/editor-store';
import type { TextCurve, TextObject } from '@ox/schema';
import { BottomSheet } from './BottomSheet';
import { CustomColorSwatch } from '../CustomColorSwatch';
import { EDITOR_FONTS, TEXT_SWATCHES, swatchLabel } from '../../lib/text-ui';
import { swatchStyle } from '../../lib/swatchStyle';
import { defaultCurveRadiusMm } from '../../lib/textCurve';
import { measureTextBoxMm } from '../../lib/textMeasure';
import {
  IconAlignCenter,
  IconAlignLeft,
  IconAlignRight,
  IconBringForward,
  IconCenterH,
  IconCenterV,
  IconColorDrop,
  IconFontAa,
  IconLayers,
  IconPencil,
  IconPosition,
  IconSendBackward,
  IconTextCurve,
  IconTextSize,
} from '../Icons';

type TextEditMode =
  | 'menu'
  | 'color'
  | 'size'
  | 'font'
  | 'content'
  | 'position'
  | 'curve'
  | 'align'
  | 'layers';

function applyCurve(
  obj: TextObject,
  curve: TextCurve,
  updateObject: (id: string, patch: Partial<TextObject>) => void,
) {
  if (curve === 'none') {
    updateObject(obj.id, { curve: 'none' });
    return;
  }
  const straight = measureTextBoxMm({
    content: obj.content,
    fontFamily: obj.fontFamily,
    fontSize: obj.fontSize,
    fontWeight: obj.fontWeight,
    lineHeight: obj.lineHeight,
    curve: 'none',
  });
  const radius =
    obj.curve !== 'none' && obj.curveRadius
      ? obj.curveRadius
      : defaultCurveRadiusMm(straight.width, obj.fontSize);
  updateObject(obj.id, { curve, curveRadius: radius });
}

export function TextAddSheet({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: () => void;
}) {
  const addText = useEditorStore((s) => s.addText);
  const [draft, setDraft] = useState('');

  function confirm(fontFamily: string) {
    addText({ content: draft.trim() || 'Texto', fontFamily });
    onAdded();
  }

  return (
    <BottomSheet title="Añadir texto" onClose={onClose} tall>
      <label className="ox-m-field">
        Texto
        <textarea
          maxLength={200}
          value={draft}
          placeholder="Escribe tu texto"
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
        />
        <span className="ox-counter">{draft.length} / 200</span>
      </label>
      <p className="ox-font-hint">Elige una tipografía</p>
      <ul className="ox-font-list ox-font-list--sheet">
        {EDITOR_FONTS.map((f) => (
          <li key={f.label}>
            <button type="button" className="ox-font-item" onClick={() => confirm(f.family)}>
              <span className="ox-font-preview" style={{ fontFamily: f.family }}>
                {draft.trim() || 'Aa'}
              </span>
              <span className="ox-font-name">{f.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </BottomSheet>
  );
}

export function TextEditSheet({ onClose }: { onClose: () => void }) {
  const selectedId = useEditorStore((s) => s.selectedId);
  const document = useEditorStore((s) => s.document);
  const activeView = useEditorStore((s) => s.activeView);
  const updateObject = useEditorStore((s) => s.updateObject);
  const centerH = useEditorStore((s) => s.centerH);
  const centerV = useEditorStore((s) => s.centerV);
  const bringForward = useEditorStore((s) => s.bringForward);
  const sendBackward = useEditorStore((s) => s.sendBackward);
  const [mode, setMode] = useState<TextEditMode>('menu');
  const [draftContent, setDraftContent] = useState<string | null>(null);

  const obj = document.views
    .find((v) => v.viewId === activeView)
    ?.objects.find((o) => o.id === selectedId && o.type === 'text') as TextObject | undefined;

  if (!obj) {
    return (
      <BottomSheet title="Texto" onClose={onClose} docked>
        <p className="ox-hint">Selecciona o añade un texto en el lienzo.</p>
      </BottomSheet>
    );
  }

  const backToMenu = () => {
    setDraftContent(null);
    setMode('menu');
  };

  if (mode === 'color') {
    return (
      <BottomSheet title="Color del texto" onBack={backToMenu} docked>
        <p className="ox-m-color-label">
          Color: <strong>{swatchLabel(obj.color)}</strong>
        </p>
        <div className="ox-m-swatch-row">
          {TEXT_SWATCHES.map((s) => (
            <button
              key={s.hex}
              type="button"
              title={s.label}
              className={`ox-m-swatch${obj.color.toLowerCase() === s.hex.toLowerCase() ? ' active' : ''}`}
              style={swatchStyle(s.hex, { borderColor: s.hex === '#ffffff' ? '#ddd' : s.hex })}
              onClick={() => updateObject(obj.id, { color: s.hex })}
            />
          ))}
          <CustomColorSwatch
            className="ox-m-swatch ox-m-swatch-rainbow"
            color={obj.color}
            onColor={(hex) => updateObject(obj.id, { color: hex })}
          />
        </div>
      </BottomSheet>
    );
  }

  if (mode === 'size') {
    const sizeMin = 4;
    const sizeMax = 400;
    const sizeVal = Math.round(obj.fontSize);
    const clamped = Math.min(sizeMax, Math.max(sizeMin, sizeVal));
    const pct = ((clamped - sizeMin) / (sizeMax - sizeMin)) * 100;
    const setSize = (n: number) =>
      updateObject(obj.id, { fontSize: Math.min(sizeMax, Math.max(sizeMin, n)) });
    return (
      <BottomSheet title="Tamaño del texto" onBack={backToMenu} onDone={backToMenu} docked>
        <div className="ox-m-size-slider">
          <input
            type="range"
            min={sizeMin}
            max={sizeMax}
            step={1}
            value={clamped}
            style={{
              background: `linear-gradient(to right, #e07a6a ${pct}%, #e5e5e5 ${pct}%)`,
            }}
            onChange={(e) => setSize(Number(e.target.value) || sizeMin)}
            aria-label="Tamaño del texto"
          />
          <input
            type="number"
            min={sizeMin}
            max={sizeMax}
            value={sizeVal}
            onChange={(e) => setSize(Number(e.target.value) || sizeMin)}
            aria-label="Valor del tamaño"
          />
        </div>
      </BottomSheet>
    );
  }

  if (mode === 'font') {
    return (
      <BottomSheet title="Elegir fuente" onBack={backToMenu} tall docked>
        <ul className="ox-font-list ox-font-list--sheet">
          {EDITOR_FONTS.map((f) => (
            <li key={f.label}>
              <button
                type="button"
                className={`ox-font-item${obj.fontFamily === f.family ? ' active' : ''}`}
                onClick={() => {
                  updateObject(obj.id, { fontFamily: f.family });
                  backToMenu();
                }}
              >
                <span className="ox-font-preview" style={{ fontFamily: f.family }}>
                  {obj.content || 'Texto'}
                </span>
                <span className="ox-font-name">{f.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </BottomSheet>
    );
  }

  if (mode === 'content') {
    const value = draftContent ?? obj.content;
    return (
      <BottomSheet title="Editar texto" onBack={backToMenu} docked>
        <label className="ox-m-field">
          <textarea
            maxLength={200}
            value={value}
            autoFocus
            onChange={(e) => setDraftContent(e.target.value)}
          />
          <span className="ox-counter">{value.length} / 200</span>
        </label>
        <button
          type="button"
          className="ox-m-confirm-btn"
          onClick={() => {
            updateObject(obj.id, { content: (draftContent ?? obj.content).slice(0, 200) });
            backToMenu();
          }}
        >
          Editar texto
        </button>
      </BottomSheet>
    );
  }

  if (mode === 'position') {
    return (
      <BottomSheet title="Posición" onBack={backToMenu} onDone={backToMenu} docked>
        <div className="ox-icon-actions ox-icon-actions--compact">
          <button type="button" onClick={centerH}>
            <IconCenterH />
            <span>Centrar H</span>
          </button>
          <button type="button" onClick={centerV}>
            <IconCenterV />
            <span>Centrar V</span>
          </button>
        </div>
      </BottomSheet>
    );
  }

  if (mode === 'curve') {
    return (
      <BottomSheet title="Texto curvo" onBack={backToMenu} onDone={backToMenu} docked>
        <div className="ox-curve-presets" role="group" aria-label="Curva del texto">
          <button
            type="button"
            title="Recto"
            className={(obj.curve ?? 'none') === 'none' ? 'active' : ''}
            onClick={() => applyCurve(obj, 'none', updateObject)}
          >
            <span className="ox-curve-ico ox-curve-ico--flat">ABCD</span>
          </button>
          <button
            type="button"
            title="Arco arriba"
            className={(obj.curve ?? 'none') === 'arc-up' ? 'active' : ''}
            onClick={() => applyCurve(obj, 'arc-up', updateObject)}
          >
            <span className="ox-curve-ico ox-curve-ico--up">ABCD</span>
          </button>
          <button
            type="button"
            title="Arco abajo"
            className={(obj.curve ?? 'none') === 'arc-down' ? 'active' : ''}
            onClick={() => applyCurve(obj, 'arc-down', updateObject)}
          >
            <span className="ox-curve-ico ox-curve-ico--down">ABCD</span>
          </button>
        </div>
        {(obj.curve ?? 'none') !== 'none' &&
          (() => {
            const radiusMin = Math.max(8, Math.round(obj.fontSize * 0.35));
            const radiusMax = 500;
            const radiusVal = Math.round(obj.curveRadius ?? 80);
            return (
              <label className="ox-curve-radius">
                Radio
                <span className="ox-hint" style={{ display: 'block', fontSize: 11, opacity: 0.7 }}>
                  Menor = más curvo
                </span>
                <div className="ox-curve-radius-row">
                  <input
                    type="range"
                    min={radiusMin}
                    max={radiusMax}
                    step={1}
                    value={Math.min(radiusMax, Math.max(radiusMin, radiusVal))}
                    onChange={(e) =>
                      updateObject(obj.id, {
                        curveRadius: Math.max(radiusMin, Number(e.target.value) || radiusMin),
                      })
                    }
                  />
                  <input
                    type="number"
                    min={radiusMin}
                    max={radiusMax}
                    value={radiusVal}
                    onChange={(e) =>
                      updateObject(obj.id, {
                        curveRadius: Math.max(radiusMin, Number(e.target.value) || radiusMin),
                      })
                    }
                  />
                </div>
              </label>
            );
          })()}
      </BottomSheet>
    );
  }

  if (mode === 'align') {
    return (
      <BottomSheet title="Alinear" onBack={backToMenu} docked>
        <div className="ox-align-row" role="group" aria-label="Alineación">
          <button
            type="button"
            className={obj.align === 'left' ? 'active' : ''}
            onClick={() => updateObject(obj.id, { align: 'left' })}
          >
            <IconAlignLeft />
          </button>
          <button
            type="button"
            className={obj.align === 'center' ? 'active' : ''}
            onClick={() => updateObject(obj.id, { align: 'center' })}
          >
            <IconAlignCenter />
          </button>
          <button
            type="button"
            className={obj.align === 'right' ? 'active' : ''}
            onClick={() => updateObject(obj.id, { align: 'right' })}
          >
            <IconAlignRight />
          </button>
        </div>
      </BottomSheet>
    );
  }

  if (mode === 'layers') {
    return (
      <BottomSheet title="Capas" onBack={backToMenu} docked>
        <div className="ox-icon-actions ox-icon-actions--compact">
          <button type="button" onClick={bringForward}>
            <IconBringForward />
            <span>Traer al frente</span>
          </button>
          <button type="button" onClick={sendBackward}>
            <IconSendBackward />
            <span>Enviar atrás</span>
          </button>
        </div>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet title="Editar texto" onDone={onClose} docked>
      <div className="ox-m-tool-grid">
        <button type="button" onClick={() => setMode('color')}>
          <IconColorDrop />
          <span>Color del texto</span>
        </button>
        <button type="button" onClick={() => setMode('size')}>
          <IconTextSize />
          <span>Tamaño del texto</span>
        </button>
        <button type="button" onClick={() => setMode('font')}>
          <IconFontAa />
          <span>Fuente</span>
        </button>
        <button
          type="button"
          onClick={() => {
            setDraftContent(obj.content);
            setMode('content');
          }}
        >
          <IconPencil />
          <span>Editar texto</span>
        </button>
        <button type="button" onClick={() => setMode('position')}>
          <IconPosition />
          <span>Posición</span>
        </button>
        <button type="button" onClick={() => setMode('curve')}>
          <IconTextCurve />
          <span>Texto curvo</span>
        </button>
        <button type="button" onClick={() => setMode('align')}>
          <IconAlignCenter size={28} />
          <span>Alinear</span>
        </button>
        <button type="button" onClick={() => setMode('layers')}>
          <IconLayers />
          <span>Capas</span>
        </button>
      </div>
    </BottomSheet>
  );
}

export function GarmentColorSheet({ onClose }: { onClose: () => void }) {
  const model = useEditorStore((s) => s.model);
  const colorKey = useEditorStore((s) => s.document.selection.colorKey);
  const setColor = useEditorStore((s) => s.setColor);
  const color = model?.colors.find((c) => c.key === colorKey);

  if (!model) return null;

  return (
    <BottomSheet title="Cambiar el color del producto" onDone={onClose}>
      <p className="ox-m-color-label">
        Color: <strong>{color?.label ?? colorKey}</strong>
      </p>
      <div className="ox-m-swatch-row">
        {model.colors.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`ox-m-swatch${colorKey === c.key ? ' active' : ''}`}
            style={swatchStyle(c.hex, { borderColor: c.isDark ? c.hex : '#ddd' })}
            title={c.label}
            onClick={() => setColor(c.key)}
          />
        ))}
      </div>
    </BottomSheet>
  );
}
